import moment from 'moment';
import JustifyPersist from '@/domain/justify/domain/justify-persist.entity';
import Customer from '@/domain/customer/entity/customer.entity';
import DateFormat from '@/shared/utils/date-format';
import ContractGroup from '@/domain/contract/entity/contract-group.entity';
import EnumObject from '../../enum-object.entity';
import ContractInstance from './contract-instance.entity';
import ContractVersion from './contract-version.entity';
import ContractBilling from './contract-billing.entity';
import ContractPlan from './contract-plan.entity';
import ContractAdditionalData from './contract-additional-data.entity';
import ContractCancellationRequest from './contract-cancellation-request.entity';

export default class Contract {
  id: string;

  code: string;

  customer: Customer;

  contractGroup: ContractGroup;

  lastUpdatePlan: String;

  contractPlan: ContractPlan;

  status: EnumObject;

  startedAt: string;

  signAt: string;

  concludedAt: string;

  instances?: Array<ContractInstance>;

  currentVersion?: ContractVersion;

  minimumBillingAmount: number;

  readjustmentStrategy: EnumObject;

  discountStrategy: EnumObject;

  discountType: EnumObject;

  periodStart: string;

  periodEnd: string;

  feeStrategy: EnumObject;

  feeType: EnumObject;

  feePeriodStart: string;

  feePeriodEnd: string;

  billing: ContractBilling;

  hasInactivation: boolean;

  isPreview: boolean;

  isCustomBilling: boolean;

  eventsReview: Array<any>;

  contractAdditionalData: ContractAdditionalData;

  cancellationRequest: ContractCancellationRequest;

  isBillingDaily: boolean;

  canceledInGroup: boolean;

  isBillingPushOrder: boolean;

  pushOrderNumber: string;

  pushOrderExpirationDate: string

  contractModel: string;

  reviewed: boolean;

  constructor(obj: any) {
    this.id = obj.id || '';
    this.code = obj.code || '';
    this.customer = Customer.of(obj.customer) || {};
    this.contractGroup = ContractGroup.of(obj.contractGroup) || {};
    this.contractPlan = obj.contractPlan || {};
    this.lastUpdatePlan = obj.lastUpdatePlan || '';
    this.status = EnumObject.of(obj.status) || {};
    this.startedAt = obj.startedAt || '';
    this.signAt = obj.signAt || '';
    this.concludedAt = obj.concludedAt || '';
    this.instances = obj.instances ? ContractInstance.ofList(obj.instances) : [];
    this.currentVersion = obj.currentVersion ? ContractVersion.of(obj.currentVersion) : undefined;
    this.minimumBillingAmount = obj.minimumBillingAmount;
    this.readjustmentStrategy = EnumObject.of(obj.readjustmentStrategy) || {};
    this.discountStrategy = EnumObject.of(obj.discountStrategy) || Contract.defaultDiscountStrategy;
    this.discountType = EnumObject.of(obj.discountType) || Contract.defaultDiscountType;
    this.periodStart = obj.periodStart || '';
    this.periodEnd = obj.periodEnd || '';
    this.feeStrategy = EnumObject.of(obj.feeStrategy) || Contract.defaultFeeStrategy;
    this.feeType = EnumObject.of(obj.feeType) || Contract.defaultFeeType;
    this.feePeriodStart = obj.feePeriodStart || '';
    this.feePeriodEnd = obj.feePeriodEnd || '';
    this.billing = ContractBilling.of(obj.billing) || {};
    this.hasInactivation = obj.hasInactivation || false;
    this.isPreview = obj.isPreview || false;
    this.isCustomBilling = obj.isCustomBilling || false;
    this.eventsReview = obj.eventsReview ? obj.eventsReview : [];
    this.contractAdditionalData = ContractAdditionalData.of(obj.contractAdditionalData);
    this.cancellationRequest = ContractCancellationRequest.of(obj.cancellationRequest);
    this.isBillingDaily = obj.isBillingDaily || false;
    this.canceledInGroup = obj.canceledInGroup || false;
    this.isBillingPushOrder = obj.isBillingPushOrder || false;
    this.pushOrderNumber = obj.pushOrderNumber || null;
    this.pushOrderExpirationDate = obj.pushOrderExpirationDate || null;
    this.contractModel = obj.contractModel || '';
    this.reviewed = obj.reviewed || false;
  }

  public static of(obj: any): Contract {
    return new Contract(obj || {});
  }

  public static ofList(obj: any[]): Contract[] {
    return obj.map(this.of);
  }

  get isActive(): boolean {
    return this.status.value === 'ACTIVE';
  }

  get isEditable() {
    return this.isActive
  }

  get isCanceled() {
    return this.status.value === 'CANCELED';
  }

  get isCanceledInGroup() {
    return this.canceledInGroup === true;
  }

  get isReleasedBilling() {
    if (!this.isActive || this.isCanceled) return false;
    if (this.isPreview) return false;
    if (this.cancellationRequest?.markedToExpire) return false;
    return !this.isPushOrderOutdated
  }

  get hasPushOrder() {
    return this.isBillingPushOrder === true;
  }

  get isPushOrderOutdated() {
    if (!this.hasPushOrder) return false;
    if (this.pushOrderNumber == null || this.pushOrderExpirationDate == null) {
      return true;
    }
    const expirationDate = moment(this.pushOrderExpirationDate);
    const today = moment();
    return today > expirationDate;
  }

  static get defaultDiscountStrategy(): EnumObject {
    return EnumObject.of({ value: 'NOT_APPLY', description: 'NÃO APLICAR DESCONTOS' });
  }

  static get defaultDiscountType(): EnumObject {
    return EnumObject.of({ value: 'PERCENT', description: 'Percentual' });
  }

  static get defaultFeeStrategy(): EnumObject {
    return EnumObject.of({ value: 'NOT_APPLY', description: 'NÃO APLICAR ACRÉSCIMOS' });
  }

  static get defaultFeeType(): EnumObject {
    return EnumObject.of({ value: 'PERCENT', description: 'Percentual' });
  }

  get version() {
    return this.currentVersion?.version;
  }

  get versionExternalId() {
    return this.currentVersion?.externalId;
  }

  get versionPercentageReadjustment() {
    return this.currentVersion?.percentageReadjustment.toFixed(4);
  }

  get versionObservation() {
    return this.currentVersion?.observation;
  }

  get applyAllReadjustmentStrategy(): boolean {
    return this.readjustmentStrategy.value === 'APPLY_ALL';
  }

  get customReadjustmentStrategy(): boolean {
    return this.readjustmentStrategy.value === 'CUSTOM';
  }

  get notApplyReadjustmentStrategy(): boolean {
    return this.readjustmentStrategy.value === 'NOT_APPLY';
  }

  get applyAllDiscountStrategy(): boolean {
    return this.discountStrategy.value === 'APPLY_ALL';
  }

  get applyAllFeeStrategy(): boolean {
    return this.feeStrategy.value === 'APPLY_ALL';
  }

  get customDiscountStrategy(): boolean {
    return this.discountStrategy.value === 'CUSTOM';
  }

  get notApplyDiscountStrategy(): boolean {
    return this.discountStrategy.value === 'NOT_APPLY';
  }

  get notApplyFeeStrategy(): boolean {
    return this.feeStrategy.value === 'NOT_APPLY';
  }

  get expiresAtFormatted(): string {
    const expiresAt = this.cancellationRequest?.expiresAt;
    if (!expiresAt) return '-'
    return DateFormat.timestampToLocal(expiresAt);
  }

  get contractModelDescription(): string {
    switch (this.contractModel) {
      case 'ERP':
        return 'ERP';
      case 'AUTOPURCHASE':
        return 'Autocompra';
      case 'ASSISTED_PURCHASE':
        return 'Venda Assistida';
      default:
        return '';
    }
  }

  get autoPurchase(): boolean {
    return this.contractModel === 'AUTOPURCHASE';
  }

  disableBilling(justify: JustifyPersist) {
    this.billing.enabled = false;
    this.billing.competenceStart = justify.competenceStart;
    this.billing.competenceEnd = justify.competenceEnd;
  }

  setHasInactivation(hasInactivation) {
    this.hasInactivation = hasInactivation;
  }

  cancel() {
    this.status.value = 'CANCELED'
    this.status.description = 'Cancelado'
  }

  swithReview(reviewed: boolean) {
    if (this.currentVersion) {
      this.currentVersion.reviewed = reviewed;
    }
  }
}
